import React, { ErrorInfo, ReactNode } from 'react';
import { RefreshCw, AlertTriangle, Copy, Trash2 } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  errorStack: string;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    errorMessage: '',
    errorStack: ''
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      errorMessage: error?.message || 'Erro desconhecido',
      errorStack: error?.stack || ''
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught React UI error:', error, errorInfo);
  }

  private handleCopyError = () => {
    const errorText = `Driver Score Error Report\n\nMessage: ${this.state.errorMessage}\n\nStack: ${this.state.errorStack}\n\nURL: ${window.location.href}\nTime: ${new Date().toISOString()}\nUserAgent: ${navigator.userAgent}`;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(errorText).then(() => {
        alert('Erro copiado! Cole e envie para o suporte.');
      });
    }
  };

  private handleReset = () => {
    // Clear potentially corrupted local state if needed
    // Unregister Service Workers to fetch fresh cache from Vercel
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function(registrations) {
        for(let registration of registrations) {
          registration.unregister();
        }
      });
    }
    
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  public render() {
    const handleNuclearWipe = () => {
      if (confirm("Isso irá deslogar o app e limpar todo o cache para corrigir erros persistentes. Deseja continuar?")) {
        localStorage.clear();
        sessionStorage.clear();
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then(regs => {
            regs.forEach(r => r.unregister());
          });
        }
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(n => caches.delete(n));
          });
        }
        setTimeout(() => {
          window.location.href = window.location.origin;
        }, 500);
      }
    };

    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4 font-sans">
          <div className="bg-slate-800 border border-slate-700 p-8 rounded-3xl shadow-2xl max-w-md w-full text-center space-y-6 animate-fade-in">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto text-red-500 mb-4">
              <AlertTriangle size={40} />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-black text-white">Oops! Algo deu errado.</h1>
              <p className="text-slate-400 text-sm leading-relaxed">
                O Driver Score encontrou um erro inesperado. Isso pode ser causado por versões antigas no cache do seu navegador.
              </p>
            </div>

            <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700 text-left">
              <p className="text-[10px] text-slate-500 uppercase font-bold mb-2">Detalhes do Erro:</p>
              <p className="text-xs font-mono text-red-400 break-all bg-black/30 p-2 rounded border border-red-900/30 max-h-32 overflow-y-auto">
                {this.state.errorMessage}
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-orange-600 hover:bg-orange-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
              >
                <RefreshCw size={18} />
                Tentar Novamente
              </button>

              <button
                onClick={handleNuclearWipe}
                className="w-full bg-slate-700 hover:bg-red-600/20 hover:text-red-400 hover:border-red-500/50 text-slate-400 font-bold py-3 px-6 rounded-xl border border-slate-600 transition-all flex items-center justify-center gap-2"
              >
                <Trash2 size={18} />
                Reparar App (Limpeza Total)
              </button>

              <button
                onClick={this.handleCopyError}
                className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-xl flex justify-center items-center gap-2 transition-all active:scale-95 text-sm"
              >
                <Copy className="w-4 h-4" />
                Copiar Erro (enviar ao suporte)
              </button>
            </div>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-6">
              Driver Score V4.0
            </p>
          </div>
        </div>
      );
    }

    return (this as any).props.children;
  }
}

